/**
 * `useTaskTeamRoster` — the Members surface roster (B6.4).
 *
 * Issue #762 (Phase 2, Settings). The members list needs three facts per
 * person, none of which is a per-person metric (§A9):
 *   - display name + initials (for the row + avatar),
 *   - role on the team (`lead`/`co_lead` → "lead", else "member"),
 *   - "on N tasks" — a neutral COUNT of how many task nodes they are
 *     assigned to, to help the lead see coverage. Explicitly NOT a ranking,
 *     score, or output measure (the gallery states this verbatim).
 *
 * We compose the two generic, taskmap-safe sources already in the module:
 *   - `useTaskTeamMembers` for name/initials (it owns the `MemberRow → name`
 *     mapping + the curated-abbreviation initials rule),
 *   - `useTaskNodes` for the assignment graph (whole-team-visible, §A8),
 * plus the raw `/members` rows for `role` (the avatar hook narrows that off).
 * Reading `GET /api/teams/{id}/members` keeps the `taskmap ⊥ scheduler`
 * boundary clean — it is the generic membership endpoint, not scheduler code.
 *
 * The assignment-count maths is a small pure fold here so the view stays a
 * dumb renderer (the founder's clean-boundary bar).
 */

import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import { useTaskNodes } from "../api/taskNodes";
import { deriveInitials } from "./useTaskTeamMembers";

type MemberRow = components["schemas"]["MemberRow"];

/** One row of the Members surface. */
export interface RosterMember {
  membershipId: string;
  name: string;
  initials: string;
  /** Raw role string (`lead` / `co_lead` / `member` / …). */
  role: string;
  /** True for `lead` / `co_lead` — drives the "lead" badge. */
  isLead: boolean;
  /** Number of distinct task nodes this member is assigned to (coverage, not a score). */
  taskCount: number;
}

const STALE_TIME_MS = 60_000;

/** Count distinct nodes each membership is assigned to, across the tree. */
export function countAssignmentsByMember(
  nodes: { assignments: { membership_id: string }[] }[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of nodes) {
    // A node could (defensively) list the same member twice — count the node
    // once per member, so this is "on N tasks", not "N assignment rows".
    const seen = new Set<string>();
    for (const a of node.assignments) {
      if (seen.has(a.membership_id)) continue;
      seen.add(a.membership_id);
      counts.set(a.membership_id, (counts.get(a.membership_id) ?? 0) + 1);
    }
  }
  return counts;
}

function isLeadRole(role: string): boolean {
  return role === "lead" || role === "co_lead";
}

export function useTaskTeamRoster(teamId: string | undefined) {
  const membersQuery = useQuery({
    queryKey: ["task-team-members", teamId ?? ""],
    enabled: Boolean(teamId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MemberRow[]>(`/api/teams/${teamId}/members`),
  });
  const nodesQuery = useTaskNodes(teamId);

  const roster = useMemo<RosterMember[]>(() => {
    const rows = Array.isArray(membersQuery.data) ? membersQuery.data : [];
    const counts = countAssignmentsByMember(Array.isArray(nodesQuery.data) ? nodesQuery.data : []);
    return rows
      .filter((row) => row.status !== "left")
      .map((row) => {
        const name = row.display_name_in_team || "Unknown";
        return {
          membershipId: row.id,
          name,
          initials: row.abbreviation?.trim() || deriveInitials(name),
          role: row.role,
          isLead: isLeadRole(row.role),
          taskCount: counts.get(row.id) ?? 0,
        };
      });
  }, [membersQuery.data, nodesQuery.data]);

  return {
    roster,
    isLoading: membersQuery.isLoading || nodesQuery.isLoading,
    isError: membersQuery.isError,
  };
}
